import { defineStore } from "pinia";
import { markRaw } from "vue";
import NotificationCountersApi from "@/services/api/notificationCountersApi";
import { createNotificationSubscription } from "@/services/notification/subscription";
import { useNotificationItemsStore, useNotificationPopupStore } from "@/stores/notificationItemsStore";
import { useUserDataStore } from "@/stores/userDataStore";
import { getCompanyId, getUserId } from "@/utils/userUtils";
import { playNotificationSound, unlockNotificationSound } from "@/utils/notification/soundUtils";
import { getNotificationSoundEnabled, setNotificationSoundEnabled } from "@/utils/notification/soundPreferenceUtils";

// Хедер владеет одним соединением с независимыми потоками списка и счётчиков.
export const useNotificationCounterStore = defineStore("notificationCounter", {
  state: () => ({
    unreadCount: null,
    otherCompanies: [],
    otherUnreadCount: null,
    companyId: null,
    userId: null,
    revision: 0,
    liveSubscription: null,
    bellPulse: 0,
    lastBellPulseAt: null,
    soundEnabled: true,
  }),

  getters: {
    badge: (state) => state.unreadCount > 99 ? "99+" : state.unreadCount > 0 ? String(state.unreadCount) : "",
    otherBadge: (state) => state.otherUnreadCount > 99 ? "+99+" : state.otherUnreadCount > 0 ? `+${state.otherUnreadCount}` : "",
    otherCompanyNotifications(state) {
      const names = new Map(useUserDataStore().companies.map((company) => [company.id, company.name]));
      // Профиль даёт только подпись: состав разрешённых компаний определяет Counters API.
      return state.otherCompanies
        .filter((counter) => counter.company_id !== state.companyId && counter.unread_count > 0)
        .map((counter) => ({ ...counter, name: names.get(counter.company_id) || `Компания #${counter.company_id}` }));
    },
  },

  actions: {
    reset() {
      this.liveSubscription?.stop();
      this.liveSubscription = null;
      this.revision += 1;
      this.clearCounts();
      this.companyId = null;
      this.userId = null;
      this.bellPulse = 0;
      this.lastBellPulseAt = null;
      this.soundEnabled = true;
    },

    clearCounts() {
      this.unreadCount = null;
      this.otherCompanies = [];
      this.otherUnreadCount = null;
    },

    isCurrent(revision) {
      return revision === this.revision &&
        this.companyId === getCompanyId() &&
        this.userId === getUserId();
    },

    startLive() {
      this.reset();
      this.companyId = getCompanyId();
      this.userId = getUserId();
      this.soundEnabled = getNotificationSoundEnabled(this.userId);
      if (!this.companyId || !this.userId) return;

      const companyId = this.companyId;
      const userId = this.userId;
      const request = this.fetchCount();
      this.liveSubscription = markRaw(createNotificationSubscription({
        companyId,
        onCreated: () => {
          if (companyId !== getCompanyId() || userId !== getUserId()) return;

          this.pulseBell();
        },
        onChange: () => {
          if (companyId !== getCompanyId() || userId !== getUserId()) return;

          useNotificationItemsStore().refreshIfOpen();
          useNotificationPopupStore().refreshIfOpen();
        },
        onCountersChange: () => {
          if (companyId !== getCompanyId() || userId !== getUserId()) return;

          return this.fetchCount();
        },
      }));
      return request;
    },

    // Серия новых уведомлений не должна непрерывно раскачивать колокольчик.
    pulseBell() {
      const now = Date.now();
      if (this.lastBellPulseAt !== null && now - this.lastBellPulseAt < 5000) return;

      this.lastBellPulseAt = now;
      this.bellPulse += 1;
      if (this.soundEnabled) playNotificationSound();
    },

    setSoundEnabled(enabled) {
      this.soundEnabled = enabled;
      setNotificationSoundEnabled(getUserId(), enabled);
      if (enabled) this.prepareSound();
    },

    prepareSound() {
      // Контекст общий с чатами; выключение уведомлений не отключает звук чатов.
      return unlockNotificationSound();
    },

    async previewSound() {
      if (!await unlockNotificationSound()) return false;
      return playNotificationSound();
    },

    async fetchCount() {
      if (!this.companyId || !this.userId) return;
      const revision = ++this.revision;
      try {
        const response = await NotificationCountersApi.index();
        if (!this.isCurrent(revision)) return;

        const counters = response.data.result;
        // Текущую компанию могли сменить в другой вкладке раньше обновления userData.
        if (counters.current_company && counters.current_company.company_id !== this.companyId) {
          this.clearCounts();
          await useUserDataStore().loadUserData(true);
          return;
        }
        this.unreadCount = counters.current_company?.unread_count ?? null;
        this.otherCompanies = counters.other_companies;
        this.otherUnreadCount = counters.other_unread_count;
      } catch (error) {
        // Не показываем устаревшее число или toast из фонового запроса хедера.
        if (this.isCurrent(revision)) this.clearCounts();
      }
    },
  },
});
